import { SearchOutlined, MergeCellsOutlined, PartitionOutlined, ExpandOutlined } from '@ant-design/icons'
import { Flex, Spin, Input, Typography, Table, Layout, Space, Button, Tooltip, Drawer, Collapse, Empty, App } from 'antd'
import { useCallback, useMemo, useState, useRef } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useCustomerProfileStore } from '@/stores/customer-profile'
import { isEmpty } from '@haina/utils-commons'
import { Conditional } from '@/components/Conditional'
import { SimpleMarkdown } from './components/simple-markdown'

import { LineItem } from './components/line-item'
import { CustomerOrderList } from './components/customer-order-list'
import { ProfileFooter } from './components/profile-footer'
import MergeCustomerProfile from './merge-profile'
import SplitCustomerProfile from './split-profile'
import dayjs from 'dayjs'

const PROFILE_CONFIG = {
  L1: { key: '1', label: 'L1 咨询阶段', extra: '顾问' },
  L2: { key: '2', label: 'L2 走团阶段', extra: '导游' },
  L3: { key: '3', label: 'L3 复盘激活', extra: '客运' },
}

const CLOSABLE_PROPS = { 'aria-label': 'Close Button' }

const TABLE_PAGINATION = {
  pageSize: 10,
  showQuickJumper: false,
  showLessItems: false,
  showSizeChanger: false,
  simple: { readOnly: true },
  size: 'small',
  position: ['topLeft'],
}

const calculateAge = (birthday) => {
  if (!birthday) return '-'
  const birthDate = new Date(birthday)
  if (isNaN(birthDate.getTime())) return '-'
  const today = new Date()
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }
  return age
}

const formatContactName = (type) => {
  switch (type) {
    case 'email': return '邮箱'
    case 'phone_number': return '手机'
    case 'whatsapp': return 'WhatsApp'
    default: return '未知'
  }
}

const formatBirthday = (dateString) => {
  if (!dateString) return '-'
  const date = dayjs(dateString)
  if (!date.isValid()) return '-'
  return date.format('YYYY-MM-DD')
}

const mapProfileToCollapseItems = (profileResult) => {
  if (!profileResult) return []
  return profileResult.map((profile) => {
    const config = PROFILE_CONFIG[profile.milestone]
    if (!config) {
      return {
        key: profile.milestone || 'unknown',
        label: `${profile.milestone || 'Unknown'} 阶段`,
        children: SimpleMarkdown(profile.insight),
        extra: <span>未知</span>,
      }
    }
    return {
      key: config.key,
      label: config.label,
      children: (
        <>
          {SimpleMarkdown(profile.insight)}
          <ProfileFooter prompt={profile?.prompt_content} input={profile?.user_query} model={profile.model} datetime={profile.updated_time} />
        </>
      ),
      extra: <span>👩‍🦱 {config.extra}</span>,
    }
  })
}

const CUSTOMER_LIST_COLUMNS = [
  {
    title: '名字/邮箱',
    dataIndex: 'customer_name',
    render: (_, customer) => (
      <Space direction="vertical">
        <Typography.Text>{customer.customer_name}</Typography.Text>
        <Typography.Text type="secondary">{customer.customer_email}</Typography.Text>
      </Space>
    ),
  },
  {
    title: '订单号',
    dataIndex: 'order_id',
    width: '150px',
  },
]

function AllInOne() {

  const [spinning, setSpinning] = useState(false)
  const [masterDrawerOpen, setMasterDrawerOpen] = useState(false)
  const [mergeDrawerOpen, setMergeDrawerOpen] = useState(false)
  const [splitDrawerOpen, setSplitDrawerOpen] = useState(false)
  const [selectedCustomerId, setSelectedCustomerId] = useState(0)
  const [collapseProfileList, setCollapseProfileList] = useState([])
  const keywordRef = useRef(null)

  const { notification } = App.useApp()

  const [
    customerList, fetchCustomerList,
    customerDetail, fetchCustomerDetail,
    contactList, fetchContactList,
    socialList, fetchSocialList,
    orderList, fetchOrderList,
    fetchOrderProfile,
    customerSummary, fetchCustomerSummary,
    customerMaster, fetchCustomerMaster,
  ] = useCustomerProfileStore(
    useShallow((s) => [
      s.customerList, s.fetchCustomerList,
      s.customerDetail, s.fetchCustomerDetail,
      s.contactList, s.fetchContactList,
      s.socialList, s.fetchSocialList,
      s.orderList, s.fetchOrderList,
      s.fetchOrderProfile,
      s.customerSummary, s.fetchCustomerSummary,
      s.customerMaster, s.fetchCustomerMaster,
    ])
  )

  const customerAge = useMemo(() => calculateAge(customerDetail.birthday), [customerDetail.birthday])

  const customerName = useMemo(
    () => (customerDetail?.first_name ?? '-') + ' ' + (customerDetail?.last_name ?? '-'),
    [customerDetail?.first_name, customerDetail?.last_name],
  )

  const contactListItems = useMemo(() =>
    contactList.map((item, index) => (
      <LineItem
        key={item.contact_id || index}
        title={formatContactName(item.contact_type)}
        value={<Button type="link" size="small">{item.contact_value}</Button>}
      />
    )), [contactList])

  const socialListItems = useMemo(() =>
    socialList.length > 0
      ? socialList.map((item, index) => (
        <LineItem key={item.id || index} variant="link" value={item.contact_value} />
      ))
      : null,
    [socialList])

  const searchCustomerList = useCallback(() => {
    const keyword = keywordRef.current.input.value
    if (isEmpty(keyword)) {
      notification.warning({ message: '请问你想搜什么？', description: '至少给我点提示吧🤣', placement: 'topLeft' })
      return
    }
    setSpinning(true)
    fetchCustomerList(keyword).finally(() => {
      setSpinning(false)
    })
  }, [fetchCustomerList, notification])

  const loadOrderProfile = useCallback((orderSn) => {
    fetchOrderProfile(orderSn).then((result) => {
      setCollapseProfileList(mapProfileToCollapseItems(result))
    })
  }, [fetchOrderProfile])

  const loadCustomerDetail = useCallback(async (customer) => {
    setSpinning(true)
    const customerId = customer.global_customer_id
    setSelectedCustomerId(customerId)

    try {
      const [, , , , , orderResult] = await Promise.all([
        fetchCustomerDetail(customerId),
        fetchCustomerSummary(customerId),
        fetchCustomerMaster(customerId),
        fetchContactList(customerId),
        fetchSocialList(customerId),
        fetchOrderList(customerId),
      ])

      if (orderResult.length > 0) {
        await loadOrderProfile(orderResult[0].order_sn)
      } else {
        setCollapseProfileList([])
      }
    } catch (error) {
      notification.error({ message: '失败是成功之母🤣', description: error.message, placement: 'topLeft' })
      setCollapseProfileList([])
    } finally {
      setSpinning(false)
    }
  }, [fetchCustomerDetail, fetchCustomerSummary, fetchCustomerMaster, fetchContactList, fetchSocialList, fetchOrderList, loadOrderProfile, notification])

  return (
    <>
      <Drawer
        title="✨ 主档案"
        size="large"
        closable={CLOSABLE_PROPS}
        onClose={() => setMasterDrawerOpen(false)}
        open={masterDrawerOpen}
      >
        {SimpleMarkdown(customerMaster?.insight)}
        <ProfileFooter prompt={customerMaster?.prompt_content} input={customerMaster?.user_query} model={customerMaster.model} datetime={customerMaster.updated_time} />
      </Drawer>
      <Drawer
        title="合并客人档案"
        size="large"
        closable={CLOSABLE_PROPS}
        onClose={() => setMergeDrawerOpen(false)}
        open={mergeDrawerOpen}
        width="960px"
      >
        <MergeCustomerProfile />
      </Drawer>
      <Drawer
        title="拆分客人档案"
        size="large"
        closable={CLOSABLE_PROPS}
        onClose={() => setSplitDrawerOpen(false)}
        open={splitDrawerOpen}
        width="960px"
      >
        <SplitCustomerProfile />
      </Drawer>
      <Spin spinning={spinning} fullscreen />
      <Layout>
        <Layout.Sider width="380" theme="light" className="relative">
          <Typography.Title level={4}>客人列表</Typography.Title>
          <Space.Compact style={{ width: '100%' }}>
            <Input allowClear placeholder="名字、邮箱、订单号" ref={keywordRef} onPressEnter={searchCustomerList} />
            <Tooltip title="搜索">
              <Button type="primary" icon={<SearchOutlined />} onClick={searchCustomerList} />
            </Tooltip>
          </Space.Compact>

          <Table
            rowKey="global_customer_id"
            pagination={TABLE_PAGINATION}
            columns={CUSTOMER_LIST_COLUMNS}
            dataSource={customerList}
            onRow={(customer) => ({
              onClick: () => loadCustomerDetail(customer),
              className: customer.global_customer_id === selectedCustomerId
                ? 'bg-ant-row-selection cursor-pointer'
                : 'cursor-pointer hover:bg-ant-row-hover',
            })}
          />
        </Layout.Sider>
        <Layout.Content style={{ minWidth: '360px' }} className="bg-white px-5">
          <Typography.Title level={4}>✨ 客人小结</Typography.Title>

          {isEmpty(customerSummary?.insight)
            ? <Flex><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /></Flex>
            : (
              <div className="space-y-2 pb-4 border-solid border-0 border-b border-gray-200">
                {SimpleMarkdown(customerSummary?.insight)}
                <ProfileFooter prompt={customerSummary?.prompt_content} input={customerSummary?.user_query} model={customerSummary.model} datetime={customerSummary.updated_time} />
              </div>
            )}

          <Typography.Title level={5}>所有订单</Typography.Title>
          <CustomerOrderList dataSource={orderList} onClick={(order) => loadOrderProfile(order.order_sn)} />

          <Typography.Title level={5}>✨ 客户旅程</Typography.Title>

          <Conditional
            condition={collapseProfileList.length > 0}
            whenTrue={
              <Collapse
                items={collapseProfileList}
                defaultActiveKey={['1', '2', '3']}
                bordered={false}
              />
            }
            whenFalse={<Flex><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /></Flex>}
          />

        </Layout.Content>
        <Layout.Sider width="400" theme="light">
          <Typography.Title level={4}>客人档案</Typography.Title>

          <div className="bg-white p-1 max-w-4xl mx-auto pb-4 border-solid border-0 border-b border-gray-200">
            <LineItem title="名字" value={customerName} />
            <LineItem title="性别" value={customerDetail?.gender} />
            <LineItem title="年龄" value={customerAge} />
            <LineItem title="生日" value={formatBirthday(customerDetail?.birthday)} />
            <LineItem title="国籍" value={customerDetail.country} />
            <Flex justify="flex-end">
              <Button type="text" icon={<ExpandOutlined />} onClick={() => setMasterDrawerOpen(true)}>更多</Button>
            </Flex>
          </div>
          <br />

          <Typography.Title level={5}>联系方式</Typography.Title>

          <div className="bg-white p-1 max-w-4xl mx-auto pb-4 border-solid border-0 border-b border-gray-200">
            {contactListItems}
            <Flex justify="flex-end">
              <Button type="text" icon={<MergeCellsOutlined />} onClick={() => setMergeDrawerOpen(true)}>合并</Button>
              <Button type="text" icon={<PartitionOutlined />} onClick={() => setSplitDrawerOpen(true)}>拆分</Button>
            </Flex>
          </div>
          <br />
          <Typography.Title level={5}>社媒</Typography.Title>
          <div className="bg-white p-1 max-w-4xl mx-auto">
            {socialListItems ?? <Flex><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /></Flex>}
          </div>

        </Layout.Sider>
      </Layout>
    </>
  )
}

export default AllInOne